import React, { useState, useEffect } from 'react';
import {
  Card, CardContent, Button, Typography, Box, Alert,
  FormControl, InputLabel, Select, MenuItem, LinearProgress,
  Divider, List, ListItem, ListItemText, Grid,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import Layout from '../../components/Layout/Layout';
import PageHeader from '../../components/Common/PageHeader';
import { adminUploadApi, downloadTemplateApi } from '../../api/uploads';
import { getPublishersApi } from '../../api/auth';

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  window.URL.revokeObjectURL(url);
}

export default function AdminUpload() {
  const [publishers, setPublishers] = useState([]);
  const [publisherId, setPublisherId] = useState('');
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getPublishersApi({ page_size: 200 }).then((r) => setPublishers(r.data.results || r.data));
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || !publisherId) { setError('Please select a publisher and file.'); return; }
    setLoading(true); setError(''); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('publisher_id', publisherId);
      const res = await adminUploadApi(fd);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplate = async () => {
    const res = await downloadTemplateApi();
    downloadBlob(res.data, 'book_upload_template.xlsx');
  };

  return (
    <Layout title="Upload Books">
      <PageHeader
        title="Upload Books (Admin)"
        breadcrumbs={[{ label: 'Admin' }, { label: 'Upload Books' }]}
        subtitle="Upload Excel or CSV file on behalf of a publisher"
        action={
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleTemplate}>
            Download Template
          </Button>
        }
      />
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Upload File</Typography>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <Box component="form" onSubmit={handleUpload}>
                <FormControl fullWidth sx={{ mb: 2 }} required>
                  <InputLabel>Select Publisher</InputLabel>
                  <Select label="Select Publisher" value={publisherId} onChange={(e) => setPublisherId(e.target.value)}>
                    {publishers.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.company_name || p.name} ({p.email})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ border: '2px dashed', borderColor: 'divider', borderRadius: 2, p: 3, textAlign: 'center', mb: 2, bgcolor: 'background.default' }}>
                  <CloudUploadIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Drag & drop or click to select (.xlsx, .xls, .csv)
                  </Typography>
                  <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files[0])}
                    style={{ display: 'block', margin: '0 auto' }} />
                  {file && <Typography variant="caption" color="primary.main" sx={{ mt: 1, display: 'block' }}>{file.name}</Typography>}
                </Box>
                {loading && <LinearProgress sx={{ mb: 2 }} />}
                <Button type="submit" variant="contained" fullWidth disabled={loading} startIcon={<CloudUploadIcon />}>
                  {loading ? 'Processing...' : 'Upload & Process'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          {result && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Upload Results</Typography>
                <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4">{result.total_records}</Typography>
                    <Typography variant="caption">Total</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">{result.success_count}</Typography>
                    <Typography variant="caption">Success</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="error.main">{result.failed_count}</Typography>
                    <Typography variant="caption">Failed</Typography>
                  </Box>
                </Box>
                {result.errors?.length > 0 && (
                  <>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="subtitle2" color="error" gutterBottom>Errors:</Typography>
                    <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {result.errors.map((e, i) => (
                        <ListItem key={i} sx={{ py: 0.5 }}>
                          <ListItemText primary={e} primaryTypographyProps={{ fontSize: 12, color: 'error.main' }} />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </CardContent>
            </Card>
          )}

        </Grid>
      </Grid>
    </Layout>
  );
}
